'use client';

import { numberInputAnatomy as parts } from '@chakra-ui/anatomy';
import {
  FormControl,
  FormLabel,
  NumberInputProps as ChakraNumberInputProps,
  FormErrorMessage,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  NumberInput,
  NumberInputField,
  createMultiStyleConfigHelpers,
  forwardRef
} from '@chakra-ui/react';
import { useRef } from 'react';

import { CommonFormProps } from '@/components/atomic/v1/types';

const { defineMultiStyleConfig, definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);
const baseStyle = definePartsStyle({
  field: {
    height: 12,
    border: '2px solid',
    borderColor: 'form/border',
    bg: 'form/bg',
    textStyle: 'body.regular'
  }
});
const myomersVariant = definePartsStyle({
  // To implement defined style in the design system for each state of the NumberInput component.
  // The default variant of NumberInput overrides the base style and this new variant is required to ensure that other variant styles are not used.
  field: {
    borderRadius: 'base',
    '::placeholder': {
      color: 'text/disabled'
    },
    _disabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
      borderColor: 'form/border',
      bg: 'form/bg:disable'
    },
    _hover: {
      borderColor: 'form/border:hover',
      bg: 'form/bg:hover',
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        borderColor: 'form/border',
        bg: 'form/bg:disable'
      }
    },
    _active: {
      borderColor: 'form/border:active',
      bg: 'form/bg:active'
    },
    _readOnly: {
      borderColor: 'form/border',
      bg: 'form/bg:readonly'
    },
    _invalid: {
      borderColor: 'form/border:error',
      bg: 'form/bg:error'
    }
  }
});

export const numberedInputTheme = defineMultiStyleConfig({
  baseStyle,
  variants: {
    myomers: myomersVariant
  },
  defaultProps: {
    variant: 'myomers'
  }
});

export type CommonInputNumberProps = {
  inputProps?: ChakraNumberInputProps;
  defaultValue?: ChakraNumberInputProps['defaultValue'];
};

export type NumberedInputProps = ChakraNumberInputProps &
  CommonFormProps &
  CommonInputNumberProps & {
    isPrice?: boolean;
    placeholder?: string;
    rightInputElement?: React.ReactNode;
  };

/**
 * Low level myOMERS NumberedInput component.
 *
 * Use the `label` prop to add a label for the NumberedInput.
 * To add $ for price use `isPrice` prop.
 *
 * Example usage:
 * ```
 *  <NumberedInput label="number" isPrice />
 * ```
 */

const NumberedInput = forwardRef<NumberedInputProps, 'input'>(
  (
    {
      label,
      error,
      isPrice = false,
      isReadOnly = false,
      isRequired = false,
      isDisabled = false,
      defaultValue = '',
      placeholder = '',
      inputProps,
      id,
      rightInputElement,
      ...rest
    },
    ref
  ) => {
    const rightInputElementRef = useRef<HTMLDivElement>(null);

    return (
      <FormControl isReadOnly={isReadOnly} isRequired={isRequired} isInvalid={!!error} isDisabled={isDisabled} id={id}>
        {label && <FormLabel>{label}</FormLabel>}
        <NumberInput ref={ref} defaultValue={defaultValue} {...rest} {...inputProps} textStyle="heading.xs">
          <InputGroup>
            {isPrice && (
              <InputLeftElement
                pointerEvents="none"
                _disabled={{
                  opacity: 0.4
                }}
              >
                $
              </InputLeftElement>
            )}

            <NumberInputField
              placeholder={placeholder}
              data-testid={`number-input-${rest?.name ?? inputProps?.name ?? defaultValue}`}
              // Set the paddingRight to the width of the rightInputElement plus 16px to ensure the input field does not overlap with the rightInputElement
              pr={rightInputElementRef.current?.offsetWidth ? `${rightInputElementRef.current.offsetWidth + 16}px` : 4}
              {...(isPrice && { pl: 9 })}
            />

            {rightInputElement && (
              <InputRightElement
                pointerEvents="none"
                width="fit-content"
                _disabled={{
                  opacity: 0.4
                }}
                ref={rightInputElementRef}
                pl={1}
                mr={4}
              >
                {rightInputElement}
              </InputRightElement>
            )}
          </InputGroup>
        </NumberInput>

        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>
    );
  }
);

export default NumberedInput;
